import { supabase } from '@/lib/supabase';
import { convertAmount, loadExchangeRates, normalizeCurrency } from '@/lib/currency';

// ─────────────────────────────────────────────────────────────
// Changement de devise du compte.
//
// Le solde est une valeur numérique : si l'on change seulement l'étiquette
// de devise, 100 FCFA devient 100 EUR. On convertit donc réellement le solde
// (portefeuille) avec les taux du jour avant d'écrire la nouvelle devise.
// ─────────────────────────────────────────────────────────────

const ZERO_DECIMAL = ['JPY', 'XOF', 'XAF', 'GNF', 'KES', 'NGN', 'CDF'];

function roundMoney(value: number, currency: string): number {
  const normalized = normalizeCurrency(currency);
  if (ZERO_DECIMAL.includes(normalized)) return Math.round(value);
  return Math.round(value * 100) / 100;
}

export interface CurrencySwitchResult {
  success: boolean;
  converted?: boolean;
  oldBalance?: number;
  newBalance?: number;
  error?: string;
}

/**
 * Convertit le solde du compte lors d'un changement de devise.
 * À appeler AVANT l'écriture du profil (la devise de référence reste l'ancienne).
 */
export async function convertAccountBalance(
  userId: number,
  fromCurrency: string,
  toCurrency: string,
): Promise<CurrencySwitchResult> {
  const from = normalizeCurrency(fromCurrency);
  const to = normalizeCurrency(toCurrency);
  if (!userId || from === to) return { success: true, converted: false };

  try {
    // Les taux doivent être disponibles avant toute conversion.
    await loadExchangeRates();

    const { data: account, error: accountError } = await supabase
      .from('users')
      .select('solde')
      .eq('id', userId)
      .maybeSingle();
    if (accountError) throw accountError;

    const current = Number(account?.solde || 0);
    if (!Number.isFinite(current) || current === 0) {
      return { success: true, converted: false, oldBalance: current, newBalance: current };
    }

    const converted = convertAmount(current, from, to);
    if (converted == null) {
      return {
        success: false,
        error: `Taux de conversion indisponible entre ${from} et ${to}. La devise n'a pas été modifiée.`,
      };
    }
    const rounded = roundMoney(converted, to);

    const { error: updateError } = await supabase
      .from('users')
      .update({ solde: rounded })
      .eq('id', userId);
    if (updateError) throw updateError;

    // Portefeuille secondaire (table wallet) : même montant, même devise.
    try {
      const { data: walletRows } = await supabase
        .from('wallet')
        .select('id, solde')
        .eq('id_user', String(userId));
      const row = walletRows?.[0];
      if (row) {
        await supabase
          .from('wallet')
          .update({ solde: String(rounded) })
          .eq('id', row.id);
      }
    } catch {
      /* table optionnelle */
    }

    // Trace l'opération : le changement de devise n'est pas une recharge.
    try {
      await supabase.from('wallet_transactions').insert({
        user_id: userId,
        type: 'ajustement',
        montant: rounded,
        methode: 'conversion_devise',
        statut: 'complete',
        reference: null,
        note: `Conversion du solde ${current} ${from} → ${rounded} ${to}`,
      });
    } catch {
      /* l'historique ne doit pas bloquer le changement de devise */
    }

    return { success: true, converted: true, oldBalance: current, newBalance: rounded };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Conversion du solde impossible.',
    };
  }
}